import { Spinner, Button, Heading, Text } from '@elladx/design-system';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { queryClient } from '../api/queryClient';
import { buildLogoutUrl, getCognitoConfig } from '../auth/cognitoConfig';
import { BILLABLE_LOCK_EVENT } from '../hooks/useBillableNavigationLock';
import { setActiveOperatorIdentity } from '../utils/operatorSession';
import { SessionActionProvider } from './SessionActionContext';
import AuthShell from './AuthShell';
function GoogleIcon() {
    return (<svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>);
}
/**
 * Single sign-on gate. Renders the app once the operator has a valid Cognito
 * session (obtained via Google), otherwise shows the shared sign-in card.
 * The authenticated branch publishes the operator identity +
 * sign-out to the app shell via SessionActionProvider.
 */
function SsoGate({ children }: {
    children: ReactNode;
}) {
    const auth = useAuth();
    const authenticatedSubject = auth.isAuthenticated && typeof auth.user?.profile.sub === 'string'
        ? auth.user.profile.sub
        : null;
    // Publish only the subject from the verified OIDC session. Child pages use it
    // synchronously to namespace billable recovery data in sessionStorage.
    setActiveOperatorIdentity(authenticatedSubject);
    const [billableLocked, setBillableLocked] = useState(false);
    useEffect(() => {
        const handleBillableLock = (event: Event) => setBillableLocked(Boolean((event as CustomEvent<boolean>).detail));
        window.addEventListener(BILLABLE_LOCK_EVENT, handleBillableLock);
        return () => window.removeEventListener(BILLABLE_LOCK_EVENT, handleBillableLock);
    }, []);
    const handleSignIn = () => {
        void auth.signinRedirect();
    };
    const handleSignOut = () => {
        const config = getCognitoConfig();
        const logoutUrl = config ? buildLogoutUrl(config) : null;
        // Drop every cached response before the session ends so the next operator
        // to sign in on this tab never sees this user's data. (The Hosted UI
        // redirect below usually discards the page anyway, but there is no logout
        // URL when no Hosted UI is configured.)
        queryClient.clear();
        setActiveOperatorIdentity(null);
        void auth.removeUser().finally(() => {
            if (logoutUrl)
                window.location.assign(logoutUrl);
        });
    };
    if (auth.isLoading) {
        return (<AuthShell>
        <Heading as="h1" variant="display" tone="default">
          <Spinner label="Checking session"/>
          Signing you in…
        </Heading>
      </AuthShell>);
    }
    if (!auth.isAuthenticated) {
        return (<AuthShell>
        <Heading as="h1" variant="display" tone="default">Sign in to EasyPost</Heading>
        <Text className="auth-lead">Continue with your Google account.</Text>
        {auth.error && (
          <Text role="alert" className="auth-error">
            We couldn’t sign you in. Check your connection and try again. If this continues, contact your administrator.
          </Text>
        )}
        <Button type="button" className="auth-google" onClick={handleSignIn} variant="primary" fullWidth={true}>
          <GoogleIcon />
          Continue with Google
        </Button>
      </AuthShell>);
    }
    const profile = auth.user?.profile;
    const userEmail = typeof profile?.email === 'string' ? profile.email : undefined;
    const userName = (typeof profile?.name === 'string' && profile.name) ||
        (typeof profile?.given_name === 'string' && profile.given_name) ||
        undefined;
    return (<SessionActionProvider value={{
            onAction: handleSignOut,
            actionLabel: 'Sign out',
            actionTitle: billableLocked
                ? 'Sign-out is locked while a billable request is processing.'
                : 'Sign out',
            disabled: billableLocked,
            userName,
            userEmail,
        }}>
      {children}
    </SessionActionProvider>);
}
export default SsoGate;
